<template>
    <div>
        <div class="alert alert-danger mt-10 mb-30" role="alert"
             v-show="hasErrors()">
            <b>Please correct the following errors:</b>
            <ul>
                <li v-for="error in errors.entries.branch_id">{{ error }}</li>
                <li v-for="error in errors.entries.entry_date">{{ error }}</li>
                <li v-for="error in errors.entries.account_head_id">{{ error }}</li>
                <li v-for="error in errors.entries.source_ledger_id">{{ error }}</li>
                <li v-for="error in errors.entries.target_ledger_id">{{ error }}</li>
                <li v-for="error in errors.entries.amount">{{ error }}</li>
                <li v-for="error in errors.entries.voucher">{{ error }}</li>
                <li v-for="error in errors.entries.notes">{{ error }}</li>
                <li v-for="error in errors.global_errors">{{ error }}</li>
            </ul>
        </div>

        <table class="account-records dataTable table table-striped">
            <tbody>
            <tr v-for="(entry, index) in entries" :key="index">
                <td>
                    <div class="d-inline-flex align-content-center justify-content-center text-primary bg-white ml-10 mb-10"
                          style="border: 1px solid steelblue; border-radius: 50%; width: 25px; height: 25px;">
                        {{ index + 1 }}
                    </div>

                    <div class="row mx-0">

                        <!-- Branch -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10" v-show="hasBranches">
                            <FormElement :error="errors.entries.branch_id[index]"
                                         label="Branch"
                                         :label-for-id="`branch_id-${index}`"
                                         label-class="required">
                                <select :id="`branch_id-${index}`"
                                        class="form-control"
                                        v-bind:class="{ 'is-invalid' : errors.entries.branch_id[index] }"
                                        v-model="entry.branch_id"
                                        @change="onBranchChanged(index, $event)">
                                    <option v-if="hasBranches" value="" selected="selected">Select...</option>
                                    <option v-for="branch in branches" :value="branch.id">
                                        {{ branch.name }}
                                    </option>
                                </select>
                            </FormElement>
                        </div>

                        <!-- Entry Date -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10">
                            <FormElement label="Date"
                                         :label-for-id="`entry_date-${index}`"
                                         label-class="required">
                                <DatePicker :required="true"
                                            :reference="index.toString()"
                                            :extras="{ endDate: '0d' }"
                                            :disabled="!hasEntryDateEditPermission"
                                            :on-change="onDateChanged"
                                            :value="entry.entry_date ? moment(entry.entry_date, 'DD-MM-YYYY').toDate() : moment().toDate()"
                                            :error="errors.entries.entry_date[index]"/>
                            </FormElement>
                        </div>

                        <!-- Type -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10">
                            <FormElement :error="errors.entries.type[index]"
                                         label="Type"
                                         :label-for-id="`type-${index}`"
                                         label-class="required">
                                <select :id="`type-${index}`"
                                        class="form-control"
                                        style="text-transform: capitalize"
                                        v-bind:class="{ 'is-invalid' : errors.entries.type[index] }"
                                        v-model="entry.type"
                                        @change="onTypeChanged(index, $event)">
                                    <option value="" selected="selected">Select...</option>
                                    <option v-for="type in accountHeadTypes" :value="type">
                                        {{ type }}
                                    </option>
                                </select>
                            </FormElement>
                        </div>

                        <!-- Head -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10"
                             v-show="entry.type !== 'transfer'">
                            <FormElement :error="errors.entries.account_head_id[index]"
                                         label="Account"
                                         :label-for-id="`account_head_id-${index}`"
                                         label-class="required">
                                <select :id="`account_head_id-${index}`"
                                        class="form-control selectpicker"
                                        data-live-search="true"
                                        v-bind:class="{ 'is-invalid' : errors.entries.account_head_id[index] }"
                                        v-model="entry.account_head_id">
                                    <template v-for="head in accountHeads[entry.type] || []" :key="head.id">
                                        <optgroup v-if="head.children.length" :label="head.name + (head.head_number ? ` — ${head.head_number}` : '')">
                                            <option v-for="child in head.children" :value="child.id">
                                                {{ child.name + (child.head_number ? ` — ${child.head_number}` : '') }}
                                            </option>
                                        </optgroup>
                                        <option v-else :value="head.id">
                                            {{ head.name + (head.head_number ? ` — ${head.head_number}` : '') }}
                                        </option>
                                    </template>
                                </select>
                            </FormElement>
                        </div>

                        <!-- Source Ledger -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10"
                             v-show="hasLedgers">
                            <FormElement :error="errors.entries.source_ledger_id[index]"
                                         label="Source Ledger"
                                         :label-for-id="`source_ledger_id-${index}`"
                                         label-class="required">
                                <select :id="`source_ledger_id-${index}`"
                                        class="form-control selectpicker"
                                        data-live-search="true"
                                        v-bind:class="{ 'is-invalid' : errors.entries.source_ledger_id[index] }"
                                        v-model="entry.source_ledger_id">
                                    <option v-for="ledger in ledgers.filter(
                                        ledgerBook =>
                                            ledgerBook.branches.filter(branch => branch.pivot.type === 'visible_as_source'
                                                && branch.id === entry.branch_id).length > 0
                                        ) || []"
                                            :value="ledger.id">
                                        {{ ledger.name }}
                                    </option>
                                </select>
                            </FormElement>
                        </div>

                        <!-- Target Ledger -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10"
                             v-show="hasLedgers && entry.type === 'transfer'">
                            <FormElement :error="errors.entries.target_ledger_id[index]"
                                         label="Target Ledger"
                                         :label-for-id="`target_ledger_id-${index}`"
                                         label-class="required">
                                <select :id="`target_ledger_id-${index}`"
                                        class="form-control selectpicker"
                                        data-live-search="true"
                                        v-bind:class="{ 'is-invalid' : errors.entries.target_ledger_id[index] }"
                                        v-model="entry.target_ledger_id">
                                    <option v-for="ledger in ledgers.filter(
                                        ledgerBook =>
                                            ledgerBook.branches.filter(branch => branch.pivot.type === 'visible_as_target'
                                                && branch.id === entry.branch_id).length > 0
                                        ) || []"
                                            :value="ledger.id">
                                        {{ ledger.name }}
                                    </option>
                                </select>
                            </FormElement>
                        </div>

                        <!-- Amount -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10">
                            <FormElement :error="errors.entries.amount[index]"
                                         label="Amount"
                                         :label-for-id="`amount-${index}`"
                                         label-class="required">
                                <input :id="`amount-${index}`"
                                       type="number"
                                       class="form-control"
                                       v-bind:class="{ 'is-invalid' : errors.entries.amount[index] }"
                                       v-model="entry.amount"
                                       required="required"/>
                            </FormElement>
                        </div>

                        <!-- Voucher -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10">
                            <FormElement label="Voucher"
                                         :label-for-id="`voucher-${index}`">
                                <input :id="`voucher-${index}`"
                                       type="text"
                                       class="form-control"
                                       v-bind:class="{ 'is-invalid' : errors.entries.voucher[index] }"
                                       v-model="entry.voucher"/>
                            </FormElement>
                        </div>

                        <!-- Notes -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10">
                            <FormElement label="Notes"
                                         :label-for-id="`notes-${index}`">
                                <input :id="`notes-${index}`"
                                       type="text"
                                       class="form-control"
                                       v-bind:class="{ 'is-invalid' : errors.entries.notes[index] }"
                                       v-model="entry.notes"/>
                            </FormElement>
                        </div>

                        <!-- Action - Delete -->
                        <div class="form-field-container col-md-4 col-lg-3 col-xxl-2 mx-10">
                            <FormElement label="">
                                <ActionButtons :entry="entry"
                                               :index="index"
                                               :remove-entry="removeEntry"/>
                            </FormElement>
                        </div>

                    </div>
                </td>
            </tr>
            </tbody>
        </table>

        <div class="mt-30">

            <!-- Add Entry -->
            <button id="add-entry-btn" type="button"
                    class="btn btn-outline btn-primary w-120 mb-20" @click="addEntry">
                <i class="fa fa-plus"></i>
                <span class="text ml-5">Add Entry</span>
            </button>

            <div class="mb-20">
                Total Amount: <b>{{ entries.map(entry => entry.amount).reduce((a, b) => a + b, 0) }}</b>
            </div>

            <!-- Submit -->
            <button type="button" class="d-block ladda-button btn btn-primary mb-20 w-120"
                    data-app-plugin="ladda" data-style="expand-left" id="account-records-submit-btn"
                    @click="submit($event)">
                Submit
            </button>

        </div>
    </div>
</template>

<script>
import {ref} from 'vue';
import moment from "moment";
import FormElement from '../../components/FormElement';
import DatePicker from "../../components/DatePicker";
import ActionButtons from "./components/ActionButtons";
import {collect} from "collect.js";

export default {
    components: {
        DatePicker,
        FormElement,
        ActionButtons,
    },
    props: {
        branches: {
            type: Object,
            required: true,
        },
        accountHeads: {
            type: Object,
            required: true,
        },
        accountHeadTypes: {
            type: Array,
            required: true,
        },
        ledgers: {
            type: Object,
            required: true,
        },
        hasBranches: {
            type: Boolean,
            required: true,
        },
        accountRecordsStoreUrl: {
            type: String,
            required: true,
        },
        createAccountRecordsUrl: {
            type: String,
            required: true,
        },
        hasEntryDateEditPermission: {
            type: Boolean,
            required: true,
        },
    },
    setup(props) {
        return {
            entries: ref([]),
            errors: ref({
                entries: {
                    branch_id: {},
                    entry_date: {},
                    type: {},
                    account_head_id: {},
                    source_ledger_id: {},
                    target_ledger_id: {},
                    amount: {},
                    voucher: {},
                    notes: {},
                },
                global_errors: []
            }),
        }
    },
    computed: {
        hasLedgers() {
            return Object.entries(this.ledgers).length > 1
        }
    },
    methods: {
        collect,
        moment(date, format) {
            moment.locale('en')
            return date ? moment(date, format) : moment()
        },
        hasErrors() {
            return Object.entries(this.errors.entries.branch_id || {}).length > 0
                || Object.entries(this.errors.entries.entry_date || {}).length > 0
                || Object.entries(this.errors.entries.account_head_id || {}).length > 0
                || Object.entries(this.errors.entries.source_ledger_id || {}).length > 0
                || Object.entries(this.errors.entries.target_ledger_id || {}).length > 0
                || Object.entries(this.errors.entries.amount || {}).length > 0
                || Object.entries(this.errors.entries.voucher || {}).length > 0
                || Object.entries(this.errors.entries.notes || {}).length > 0
                || this.errors.global_errors.length > 0
        },
        onDateChanged(ref, date) {
            this.entries[ref].entry_date = date
        },
        onTypeChanged(index, event) {
            this.$nextTick(() => {
                $(`#account_head_id-${index}`).selectpicker('refresh')
            })
        },
        onBranchChanged(index, event) {
            this.$nextTick(() => {
                $(`#source_ledger_id-${index}, #target_ledger_id-${index}`).selectpicker('refresh')
            })
        },
        addEntry() {
            this.entries.push({
                branch_id: this.hasBranches ? null : this.branches[0].id,
                entry_date: this.entries[this.entries.length - 1]?.entry_date,
                type: null,
                account_head_id: null,
                source_ledger_id: Object.entries(this.ledgers).length === 1 ? Object.values(this.ledgers)[0].id : null,
                target_ledger_id: null,
                amount: null,
                voucher: null,
                notes: null,
            })
            this.$nextTick(() => {
                $('.account-records .selectpicker').eq(this.entries.length - 1).selectpicker('refresh')
            })
            if (!this.hasBranches) {
                this.onBranchChanged(this.entries.length - 1)
            }
        },
        removeEntry(index) {
            this.entries.splice(index, 1)
        },
        submit(event) {
            event.preventDefault()

            fetch(this.accountRecordsStoreUrl,
                {
                    method: 'post',
                    headers: new Headers({
                        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/json'
                    }),
                    credentials: 'include',  // adds cookie header
                    body: JSON.stringify(this.submissionData())
                }
            ).then(async response => {
                if (response.status === 422) {
                    const json = await response.json()
                    this.showSubmissionErrors(this.transformSubmissionErrors(json.errors))
                    window.Ladda.stopAll()
                } else if (!response.ok) {
                    const error = await response.text()
                    toastr.error(`Failed posting account records: ${error}`)
                    window.Ladda.stopAll()
                } else if (response.redirected) {
                    toastr.success('Account records created.')
                    window.location.href = response.url
                }
            })
        },
        submissionData() {
            return {
                entries: this.entries
            }
        },
        transformSubmissionErrors(serverSideErrors) {
            const errors = {
                entries: {
                    branch_id: {},
                    entry_date: {},
                    type: {},
                    account_head_id: {},
                    source_ledger_id: {},
                    target_ledger_id: {},
                    amount: {},
                    voucher: {},
                    notes: {},
                },
                global_errors: []
            }

            for (let prop in serverSideErrors) {
                if (prop.startsWith('entries')) {
                    const propParts = prop.split('.')
                    errors.entries[propParts[2]][propParts[1]] = serverSideErrors[prop][0]
                } else {
                    errors.global_errors.push(serverSideErrors[prop])
                }
            }

            return errors
        },
        showSubmissionErrors(errors) {
            console.log(errors)
            this.errors = errors
            toastr.error(`Please correct the errors specified.`)
            window.scrollTo({top: 0, behavior: 'smooth'})
        }
    },
    mounted() {
        this.addEntry()

        this.$nextTick(() => {
          window.disableMouseWheelOnNumberInputs()
        })
    },
}
</script>

<style>
    table.account-records .form-control-input {
        padding: 0;
    }
</style>
